import { Injectable } from '@angular/core';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  // [START] Added RedirectCommand import - 2025-09-25
  // Author: Ketan Shetye
  // -------------------
  // Reason: Required for Angular v18 guard redirect (RedirectCommand instead of navigate)
  RedirectCommand,
  CanActivate
  // -------------------
  // [END] - 2025-09-25
} from '@angular/router';
import { WorkflowContextService, SecurityService, DataService } from '.';
import { Observable, of, tap } from 'rxjs';
import { map, switchMap, concatMap, catchError } from 'rxjs/operators';
import { ClientService } from './client.service';
import { WorkflowService } from './workflow.service';
import { ItemSearchOptionField, ItemSearchOptionFieldOption } from 'src/app/components/filter-list/models/filterClasses';



@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private router: Router,
    private _context: WorkflowContextService,
    private _securitySvc: SecurityService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (this._context.authToken && this._context.user) {
      // logged in AND user is set, so return true
      return true;
    }

    // not logged in so redirect to login page with the return url
    // [START] - 2025-09-25
    // Author: Ketan Shetye
    // -------------------
    // Reason: Updated for Angular v18 guard redirect.
    // Replaced imperative `this.router.navigate(...)` with `RedirectCommand`
    return new RedirectCommand(
      this.router.createUrlTree(['/auth/login'], {
        queryParams: { returnURL: state.url }
      }),
      { replaceUrl: true }
    );
    // -------------------
    // [END] - 2025-09-25
  }
}

@Injectable({ providedIn: 'root' })
export class ActionGuard {
  constructor(
    private router: Router,
    private _context: WorkflowContextService,
    private _securitySvc: SecurityService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> | boolean | RedirectCommand {
    return this._securitySvc.isLoginEntitled(next.data.action).pipe(
      map(result => {
        if (!result) {
          // [START] - 2025-09-25
          // Author: Ketan Shetye
          // -------------------
          // Reason: Updated for Angular v18 guard redirect.
          // Replaced imperative `this.router.navigate(...)` with `RedirectCommand`
          return new RedirectCommand(
            this.router.createUrlTree(['/auth/login'], {
              queryParams: { returnURL: state.url }
            }),
            { replaceUrl: true }
          );
          // -------------------
          // [END] - 2025-09-25
        }

        return result;
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ActionsGuard {
  constructor(private router: Router, private _securitySvc: SecurityService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> | boolean | RedirectCommand {
    return this._securitySvc
      .isLoginEntitled(null, null, next.data.actions)
      .pipe(
        map(result => {
          if (!result) {
            // [START] - 2025-09-25
            // Author: Ketan Shetye
            // -------------------
            // Reason: Updated for Angular v18 guard redirect.
            // Replaced imperative `this.router.navigate(...)` with `RedirectCommand`
            return new RedirectCommand(
              this.router.createUrlTree(['/admin/global/unauthorized'], {
                queryParams: { returnURL: state.url }
              }),
              { replaceUrl: true }
            );
            // -------------------
            // [END] - 2025-09-25
          }

          return result;
        })
      );
  }
}

@Injectable({ providedIn: 'root' })
export class ClientAdminGuard {
  constructor(
    private router: Router,
    private _context: WorkflowContextService,
    private _securitySvc: SecurityService,
    private _clientSvc: ClientService,
    private _dataSvc: DataService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> {
    return this._securitySvc.isSystemAdministrator().pipe(
      concatMap(isSysAdmin => {
        this._securitySvc.clearPermissionCache();

        return this._clientSvc.getAdminClients().pipe(
          concatMap(clients => {
            if (clients?.length === 1 && !isSysAdmin) {
              const client = clients[0];
              const newLayout = client?.useNewLayout ?? false;

              if (!newLayout) {
                return of(
                  new RedirectCommand(
                    this.router.createUrlTree([
                      '/admin/jurisdiction',
                      client.id
                    ]),
                    { replaceUrl: true }
                  )
                );
              }
              // new layout logic
              return this._dataSvc.getClientDepartments(client.id).pipe(
                map(departments => {
                  if (departments && departments.length > 0) {
                    this._context.staffDepartmentId$.emit(departments[0].id);
                    return new RedirectCommand(
                      this.router.createUrlTree([
                        '/admin/jurisdiction',
                        client.id,
                        'department',
                        departments[0].id
                      ]),
                      { replaceUrl: true }
                    );
                  } else {
                    this._context.staffDepartmentId$.emit(null);
                    return new RedirectCommand(
                      this.router.createUrlTree([
                        '/admin/jurisdiction',
                        client.id
                      ]),
                      { replaceUrl: true }
                    );
                  }
                })
              );
            }
            return of(true);
          })
        );
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class CompletedApplicationGuard {
  constructor(private router: Router, private _workflowSvc: WorkflowService) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> | boolean | RedirectCommand {
    return this._workflowSvc
      .isWorkflowApplicationInProgress(next.params.applicationId)
      .pipe(
        map(response => {
          if (response === false) {
            const historyState = history.state;

            // [START] - 2025-09-25
            // Author: Ketan Shetye
            // -------------------
            // Reason: Updated for Angular v18 guard redirect.
            // Replaced imperative `this.router.navigate(...)` with `RedirectCommand`
            history.replaceState(
              {},
              'DataGrid',
              historyState && historyState.navigationPath
                ? historyState.navigationPath
                : null
            );
            history.pushState(
              {},
              '',
              `/application/workflow-application-summary/${next.params.applicationId}`
            );

            return new RedirectCommand(
              this.router.createUrlTree([
                '/application/workflow-application-summary/',
                next.params.applicationId
              ]),
              { replaceUrl: true }
            );
            // -------------------
            // [END] - 2025-09-25
          }

          return true;
        })
      )
  }
}
@Injectable({ providedIn: 'root' })
export class CompletedJanusAdminNewApplicationGuard {
  constructor(
    private router: Router,
    private _workflowSvc: WorkflowService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> | boolean | RedirectCommand {
    const applicationId = next.params.applicationId;
    return this._workflowSvc
      .isWorkflowApplicationInProgress(applicationId)
      .pipe(
        map(response => {
          if (response === false) {
            const segments = state.url.split('/');
            const deptId =  segments[segments.indexOf('department') + 1];
            const jurisdictionId = segments[segments.indexOf('jurisdiction') + 1];
            return new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      jurisdictionId,
                      'department',
                      deptId,
                      'workflow-applications',
                      'data'
                    ],
                    {
                      queryParams: {
                        id: applicationId,
                        s: 'external'
                      },
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
          }

          return true;
        })
      );
  }
}

@Injectable({ providedIn: 'root' })
export class ClientIdGuard {
  constructor(
    private router: Router,
    private _context: WorkflowContextService,
    private _clientSvc: ClientService,
    private _securitySvc: SecurityService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const clientId = route.firstChild?.paramMap.get('clientId');

    if (!clientId) {
      return true;
    }

    return this._securitySvc.canAccessClient(clientId).pipe(
      switchMap(canAccess => {
        if (!canAccess) {
          return of(new RedirectCommand(
            this.router.createUrlTree(['/admin/global/unauthorized']),
            { replaceUrl: true }
          ));
        }

        if (this._context.client?.id === clientId) {
          return of(true);
        }

        return this._clientSvc.getPublicClient(clientId).pipe(
          map(client => {
            if (client) {
              this._context.client$.emit(client);
            }

            return true;
          })
        );
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class JanusLayoutGuard implements CanActivate {
  constructor(private router: Router, private _clientSvc: ClientService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> {
    const clientId = route.paramMap.get('clientId');

    if (!clientId) return of(true);

    // Use the public client endpoint (only useNewLayout is needed here) so
    // users without CLIENTS_MANAGE / CLIENTS_MANAGE_SETTINGS can still
    // activate this route via role membership.
    return this._clientSvc.getPublicClient(clientId).pipe(
      map(client => {
        const useNewLayout = client?.useNewLayout ?? false;

        if (!useNewLayout) {
          return new RedirectCommand(
            this.router.createUrlTree(['/admin/jurisdiction', clientId]),
            { replaceUrl: true }
          );
        }

        return true;
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class GeopermitLayoutGuard implements CanActivate {
  constructor(
    private router: Router,
    private _clientSvc: ClientService,
    private _dataSvc: DataService,
    private context: WorkflowContextService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> {
    // Skip for renewal details page
  if (state.url.includes('/renewals/details/')) {
    return of(true);
  }

    const clientId = route.paramMap.get('clientId');

    if (!clientId) return of(true);

    // Use the public client endpoint (only useNewLayout is needed here) so
    // users without CLIENTS_MANAGE / CLIENTS_MANAGE_SETTINGS can still
    // activate this route via role membership.
    return this._clientSvc.getPublicClient(clientId).pipe(
      concatMap(client => {
        const useNewLayout = client?.useNewLayout ?? false;

        if (useNewLayout) {
          return this._dataSvc.getClientDepartments(clientId).pipe(
            map(depts => {
              const deptId = depts?.[0]?.id;
              this.context.staffDepartmentId$.emit(depts?.[0]?.id);
              return new RedirectCommand(
                this.router.createUrlTree([
                  '/admin/jurisdiction',
                  clientId,
                  'department',
                  deptId
                ]),
                { replaceUrl: true }
              );
            })
          );
        } else {
          this.context.staffDepartmentId$.emit(null);
        }

        return of(true);
      })
    );
  }
}


@Injectable({ providedIn: 'root' })
export class CompletedApplicationViewLayoutGaurd implements CanActivate {
  constructor(
    private workflowSvc: WorkflowService,
    private router: Router,
    private context: WorkflowContextService
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const applicationId = route.params['applicationId'];
    if(!applicationId) return of(true);

    return this.workflowSvc
      .getWorkflowApplicationRoutingDetails(applicationId)
      .pipe(
        concatMap(response => {
          const useNewLayout = response?.useNewLayout ?? false;
          const clientId = response?.clientId ?? '';
          const deptId = response?.departmentId ?? '';
          const submitterId = response?.submitterId ?? '';
          const userId = this.context?.user?.id ?? '';

          if(useNewLayout === true){
            if (!clientId || !submitterId || !userId) return of(true);
            if(submitterId === userId){
              return of(
                new RedirectCommand(
                  this.router.createUrlTree([
                    '/jurisdiction',
                    clientId,
                    'applicant-home',
                    'my-applications',
                    'workflow-application-print-summary',
                    applicationId
                  ]),
                  { replaceUrl: true }
                )
              );

            }else{
              if(!deptId) return of(true);
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      clientId,
                      'department',
                      deptId,
                      'workflow-applications',
                      'data'
                    ],
                    {
                      queryParams: {
                        id: applicationId,
                        s: 'external'
                      },
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
              );
            }

          }else{
            return of(true);
          }

        })
      );
  }
}
@Injectable({ providedIn: 'root' })
export class RenewalApplicationViewDetailsLayoutGaurd implements CanActivate {
  constructor(
    private workflowSvc: WorkflowService,
    private router: Router,
    private context: WorkflowContextService
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const registrationId = route.params['registrationId'];
    if(!registrationId) return of(true);

    return this.workflowSvc.getRegistration(registrationId).pipe(
  concatMap(response => {
    const applicationId = response?.registerApplicationId;

    if (!applicationId) {
      return of(true);
    }

    return this.workflowSvc
      .getWorkflowApplicationRoutingDetails(applicationId)
      .pipe(
        concatMap(response => {
          const useNewLayout = response?.useNewLayout ?? false;
          const clientId = response?.clientId ?? '';
          const deptId = response?.departmentId ?? '';
          if (useNewLayout === true) {
            if (!clientId && !deptId) return of(true);
              // admin route
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      clientId,
                      'department',
                      deptId,
                      'renewals'
                    ],
                    {
                      queryParams: {
                        // Added by Amisha on 06/12/2026
                        // Reason: Janus renewals restores inline details using the registration id from query params.
                        view:'details',
                        id:registrationId,
                        registrationId:registrationId
                      },
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
              );

          } else {
            // go to geopermits route
            return of(true);
          }
        })
      );
  })
);
  }
}
@Injectable({ providedIn: 'root' })
export class ApplicationViewLayoutGaurd implements CanActivate {
  constructor(
    private workflowSvc: WorkflowService,
    private router: Router,
    private context: WorkflowContextService
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const applicationId = route.params['applicationId'];
    if (!applicationId) return of(true);

    return this.workflowSvc
      .getWorkflowApplicationRoutingDetails(applicationId)
      .pipe(
        concatMap(response => {
          const useNewLayout = response?.useNewLayout ?? false;
          const clientId = response?.clientId ?? '';
          const deptId = response?.departmentId ?? '';
          const submitterId = response?.submitterId ?? '';
          const userId = this.context?.user?.id ?? ''; // Code edited by Sanchit on 05/06/2026
        //   const userId = this.context.user.id ?? '';
          if (useNewLayout === true) {
            if (!clientId || !userId) return of(true);
            if(!submitterId){
              // start new application in staff side
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      clientId,
                      'department',
                      deptId,
                      'workflow-applications',
                      'data',
                      'application',
                      applicationId
                    ],
                  ),
                  { replaceUrl: true }
                )
              );
            }
            // go to janus route
            if (submitterId === userId) {
              // applicant route
              return of(
                new RedirectCommand(
                  this.router.createUrlTree([
                    '/jurisdiction',
                    clientId,
                    'applicant-home',
                    'my-applications',
                    'workflow-application',
                    applicationId
                  ],
                  {
                    queryParams: {
                      s: 'external'
                    },
                    queryParamsHandling: 'replace'
                  }),
                  { replaceUrl: true }
                )
              );
            } else {
              if (!deptId) return of(true);
              // admin route
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      clientId,
                      'department',
                      deptId,
                      'workflow-applications',
                      'data'
                    ],
                    {
                      queryParams: {
                        id: applicationId,
                        s: 'external'
                      },
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
              );
            }
          } else {
            // go to geopermits route
            return of(true);
          }
        })
      );
  }
}
@Injectable({ providedIn: 'root' })
export class ApplicationActivityViewLayoutGaurd implements CanActivate {
  constructor(
    private workflowSvc: WorkflowService,
    private router: Router,
    private context: WorkflowContextService
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const applicationId = route.params['applicationId'];
    const applicationActivityId = route.params['activityDataId'];
    if (!applicationId && !applicationActivityId) return of(true);

    return this.workflowSvc
      .getWorkflowApplicationRoutingDetails(applicationId)
      .pipe(
        concatMap(response => {
          const useNewLayout = response?.useNewLayout ?? false;
          const clientId = response?.clientId ?? '';
          const deptId = response?.departmentId ?? '';
          const submitterId = response?.submitterId ?? '';
          const userId = this.context?.user?.id ?? ''; // Code edited by Sanchit on 05/06/2026
        //   const userId = this.context.user.id ?? '';
          if (useNewLayout === true) {
            if (!clientId || !submitterId || !userId) return of(true);
            // go to janus route
            if (submitterId === userId) {
              // applicant route
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/jurisdiction',
                      clientId,
                      'applicant-home',
                      'my-applications',
                      'workflow-application',
                      applicationId,
                      applicationActivityId
                    ],
                    {
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
              );
            } else {
              if (!deptId) return of(true);
              // admin route
              return of(
                new RedirectCommand(
                  this.router.createUrlTree(
                    [
                      '/admin/jurisdiction',
                      clientId,
                      'department',
                      deptId,
                      'workflow-applications',
                      'data'
                    ],
                    {
                      queryParams: {
                        id: applicationId,
                        s: 'external',
                        activityDataId: applicationActivityId,
                        showAppView: 1
                      },
                      queryParamsHandling: 'replace'
                    }
                  ),
                  { replaceUrl: true }
                )
              );
            }
          } else {
            // go to geopermits route
            return of(true);
          }
        })
      );
  }
}

@Injectable({ providedIn: 'root' })
export class ApplicantJanusGuard implements CanActivate {
  constructor(private router: Router, private _clientSvc: ClientService) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | RedirectCommand> {
    const clientId = route.paramMap.get('clientId');

    if (!clientId) return of(true);

    return this._clientSvc.getPublicClient(clientId).pipe(
      map(client => {
        const useNewLayout = client?.useNewLayout ?? false;

        if (!useNewLayout) {
          return new RedirectCommand(
            this.router.createUrlTree(['/jurisdiction', clientId]),
            { replaceUrl: true }
          );
        }

        return true;
      })
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ApplicantGeopermitGuard implements CanActivate {
  constructor(private router: Router, private _clientSvc: ClientService) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const clientId = route.paramMap.get('clientId');

    if (!clientId) return of(true);

    return this._clientSvc.getPublicClient(clientId).pipe(
      map(client => {
        const useNewLayout = client?.useNewLayout ?? false;

        if (useNewLayout) {
          return new RedirectCommand(
            this.router.createUrlTree([
              '/jurisdiction',
              clientId,
              'applicant-home',
              'apply-for-permit'
            ]),
            { replaceUrl: true }
          );
        }

        return true;
      })
    );
  }
}
@Injectable({ providedIn: 'root' })
export class ContractorRegistrationDetailsLayoutGuard
  implements CanActivate
{
  constructor(
    private dataService: DataService,
    private workflowSvc: WorkflowService,
    private context: WorkflowContextService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | RedirectCommand> {
    const registrationId = route.paramMap.get('registrationId');

    const clientId =
      this.context.client?.id ||
      (this.context as any).clientId ||
      '';

    const userId =
      this.context?.user?.id ||
      (this.context as any).userId ||
      (this.context as any).currentUser?.id ||
      '';

    if (!registrationId || !clientId) {
      return of(true);
    }

    const filters = this.buildFilters(userId);

    return this.dataService
      .searchContractorRegistrations(
        clientId,
        'registeredOn',
        true,
        filters,
        1,
        100,
        false
      )
      .pipe(
        switchMap(result => {
          const registrations =
            (result as any)?.contractorRegistrations ||
            (result as any)?.registrations ||
            (result as any)?.items ||
            (result as any)?.data ||
            [];

          const registration = registrations.find(
            (item: any) =>
              item?.id === registrationId ||
              item?.contractorRegistrationId === registrationId
          );

          if (!registration) {
            return of(true);
          }

          const applicationId =
            registration?.registerApplicationId ||
            registration?.registerApplication?.id ||
            registration?.applicationId ||
            registration?.workflowApplicationId ||
            '';

          if (!applicationId) {
            return of(true);
          }

          return this.workflowSvc
            .getWorkflowApplicationRoutingDetails(applicationId)
            .pipe(
              map(routingDetails => {
                const useNewLayout =
                  routingDetails?.useNewLayout ?? false;

                const routingClientId =
                  routingDetails?.clientId || clientId;

                if (!useNewLayout || !routingClientId) {
                  return true;
                }

                return new RedirectCommand(
                  this.router.createUrlTree([
                    '/jurisdiction',
                    routingClientId,
                    'applicant-home',
                    'my-contractor-registrations',
                    'details',
                    registrationId
                  ]),
                  { replaceUrl: true }
                );
              }),
              catchError(() => of(true))
            );
        }),
        catchError(() => of(true))
      );
  }

  private buildFilters(
    userId: string
  ): ItemSearchOptionField[] {
    const filters: ItemSearchOptionField[] = [
      {
        title: 'status',
        options: [
          new ItemSearchOptionFieldOption({
            title: '',
            selected: true,
            filterText: '',
            searchText: '0,1,2,4,5,6,7',
            strOperator: 'in'
          })
        ]
      }
    ];

    if (userId) {
      filters.push({
        title: 'userId',
        options: [
          new ItemSearchOptionFieldOption({
            title: '',
            selected: true,
            filterText: '',
            searchText: userId,
            strOperator: 'is'
          })
        ]
      });
    }

    return filters;
  }
}